import Axios from 'axios';
import { FetchDocumentsCountResponse, ListDocumentRequest, ListDocumentResponse } from './models';
import { clientIntegrationSvcBaseUrl } from './utils';
/**
 * @deprecated. To be removed when all outbound api's are migrated.
 * Lists documents from the specified base URL.
 *
 * This function sends a POST request to the document filter endpoint with the provided parameters.
 * The response is expected to be a ListDocumentResponse object.
 *
 * @param {string} baseUrl - The base URL of the API.
 * @param {string} documentType - The type of documents to filter.
 * @param {number} limit - The maximum number of documents to return.
 * @param {number} offset - The offset for pagination.
 * @param {ListDocumentRequest} data - The request payload containing filter criteria.
 * @returns {Promise<ListDocumentResponse>} - A promise that resolves to the filtered documents.
 * @throws Will throw an error if the request fails.
 */
export const listDocuments = async (
  baseUrl: string,
  documentType: string,
  limit: number,
  offset: number,
  data: ListDocumentRequest
): Promise<ListDocumentResponse> => {
  try {
    const res = await Axios<ListDocumentResponse>({
      url: `${baseUrl}/documents/${documentType}:filter?limit=${limit}&offset=${offset}`,
      method: 'POST',
      data,
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Downloads bulk documents from the specified base URL.
 *
 * This function sends a POST request to the bulk download endpoint with the provided document IDs.
 * The response is expected to be a blob, which can be used to download files.
 *
 * @param {string} baseUrl - The base URL of the API.
 * @param {string[]} documentIds - An array of document IDs to be downloaded.
 * @returns {Promise<Blob>} - A promise that resolves to the downloaded blob data.
 * @throws Will throw an error if the request fails.
 */
export const downloadBulkDocuments = async (baseUrl: string, documentIds: string[]): Promise<Blob> => {
  try {
    const res = await Axios<Blob>({
      url: `${baseUrl}/documents/accounts:bulk-download`,
      method: 'POST',
      data: {
        documentIds,
      },
      responseType: 'blob',
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Downloads a single document from the specified URL.
 *
 * @param baseUrl - The base URL of the API.
 * @param entityType - The type of the entity (e.g., user, contracts, accounts).
 * @param entityId - The ID of the entity.
 * @param documentId - The ID of the document to be downloaded.
 * @returns A promise that resolves to the downloaded document data as a blob.
 * @throws Will throw an error if the request fails.
 */
export const downloadSingleDocument = async (
  baseUrl: string,
  entityType: string,
  entityId: string,
  documentId: string
) => {
  try {
    const res = await Axios({
      url: `${baseUrl}/${entityType}/${entityId}/documents/${documentId}`,
      method: 'GET',
      responseType: 'blob',
    });
    return res;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * @deprecated - Remove this when outbound migration is complete.
 * Fetches the total count of documents for a given document type from the API.
 *
 * Sends a POST request to the count endpoint and returns an object with totalCount.
 *
 * @param documentType - The type of documents to count (e.g., 'accounts')
 * @param data - The request payload containing filter criteria
 * @returns An object with the totalSize property
 * @throws Will throw an error if the request fails or the response is invalid
 */
export const fetchDocumentsCount = async (
  documentType: string,
  data: ListDocumentRequest
): Promise<FetchDocumentsCountResponse> => {
  try {
    const res = await Axios({
      url: `${clientIntegrationSvcBaseUrl}/api/v1/aperio/documents/${documentType}:count`,
      method: 'POST',
      data,
    });
    const { totalSize } = res.data;
    return { totalSize };
  } catch (error) {
    console.error(error);
    return { totalSize: 0 };
  }
};
